import { useDebounce, useRequest } from "ahooks";
import { AdminContext } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";
import { Pagination as PaginationData } from "pages/Main/Components/AdminPanel/Options";
import {
  forceCancelTransaction,
  forceCompleteTransaction,
  getTransactionMetrics,
  getTransactions,
  InusranceStatuses,
  PaymentMethod,
  ProcessType,
  retryWithNewPayout,
  softRetryPayout,
  SortInsuranceBy,
  TransactionAction,
} from "helpers/backend_helper";
import {
  ENTITY_PER_PAGE,
  GetPurposeCategoryEnum,
  PROP_NAME_CITY,
  PROP_NAME_FROM_DATE_SEARCH_MAIN,
  PROP_NAME_INPUT_TEXT,
  PROP_NAME_METHOD,
  PROP_NAME_PURPOSE,
  PROP_NAME_SKIP_ADS,
  PROP_NAME_SORTBY,
  PROP_NAME_TO_DATE_SEARCH_MAIN,
  PURPOSE_OPTIONS_V2,
} from "helpers/constants";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./index.module.css";

import c2cToken from "assets/svg/c2c-token.svg";
import hash from "assets/svg/Hash.svg";
import calendarDots from "assets/svg/CalendarDots.svg";
import sortIcon from "assets/svg/SortAscending.svg";
import userCircle from "assets/svg/UserCircle.svg";
import crosshair from "assets/svg/Crosshair.svg";
import currencyEur from "assets/svg/CurrencyEur.svg";
import spinnerGap from "assets/svg/SpinnerGap.svg";
import arrowDownGreen from "assets/svg/ArrowDownGreen.svg";
import arrowUpRed from "assets/svg/ArrowUpRed.svg";
import checkCircleGreen from "assets/svg/CheckCircleGreen.svg";
import arrowCircleUpRed from "assets/svg/ArrowCircleUpRed.svg";
import lockSimple from "assets/svg/LockSimple.svg";
import clockCountdownBlue from "assets/svg/ClockCountdownBlue.svg";
import warningLight from "assets/svg/WarningLight.svg";
import arrowCircleDownGreen from "assets/svg/arrowCircleDownGreen.svg";

import { InfoComponent } from "Components/InfoComponent";
import { onSort } from "helpers/helper";
import { formatMoney, formatNumber, formatTransactionDateTime } from "helpers/format";
import { TableAntdV2 } from "Components/TableV2";
import { Button, Tooltip } from "antd";
import { UserNameRatingPhotoComponent } from "Components/UserNameRatingPhotoComponent";
import { CustomButton } from "Components/CustomButton";
import { DealsInfo } from "Components/DealsInfo";
import { CustomPagination } from "Components/CustomPagination";
import { ModalConfirm } from "./ModalConfirm";

const divStatuses = {
  completed: (
    <div className={`${styles.completed} ${styles.divStatus}`}>
      <img src={checkCircleGreen} alt="check circle green" />
      <span>Completed</span>
    </div>
  ),
  funded: (
    <div className={styles.divStatus}>
      <img src={arrowCircleUpRed} alt="check circle green" />
      <span>Funded</span>
    </div>
  ),
  refunded: (
    <div className={styles.divStatus}>
      <img src={arrowCircleDownGreen} alt="check circle green" />
      <span>Refunded</span>
    </div>
  ),
  holded: (
    <div className={styles.divStatus}>
      <img src={lockSimple} alt="check circle green" />
      <span>Holded</span>
    </div>
  ),
  unholded: (
    <div className={styles.divStatus}>
      <img src={lockSimple} alt="check circle green" />
      <span>Unholded</span>
    </div>
  ),
  pending: (
    <div className={`${styles.pending} ${styles.divStatus}`}>
      <img src={clockCountdownBlue} alt="check circle green" />
      <span>Pending</span>
    </div>
  ),
  failed: (
    <div className={`${styles.failed} ${styles.divStatus}`}>
      <img src={warningLight} alt="check circle green" />
      <span>Failed</span>
    </div>
  ),
};

type Props = {
  status: InusranceStatuses;
};

export const RequestTransactionsIns = ({ status }: Props) => {
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const { localCountry } = useContext(AdminContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [skipAds, setSkipAds] = useState(+searchParams.get(PROP_NAME_SKIP_ADS) || 0);
  const [pagination, setPagination] = useState<PaginationData>({ skip: 0, take: ENTITY_PER_PAGE, total: 0 });
  const sortByFilter = searchParams.get(PROP_NAME_SORTBY);
  const sortBy = (sortByFilter as SortInsuranceBy) || "";
  const [selectedId, setSelectedId] = useState("");
  const [isDealOpen, setIsDealOpen] = useState(false);
  const [isUserInput, setIsUserInput] = useState(false);
  const isRefresh = useDebounce(searchParams, { wait: 100 });
  const transactionIdOnAction = useRef("");

  const skipAdsFilter = searchParams.get(PROP_NAME_SKIP_ADS);
  const searchText = searchParams.get(PROP_NAME_INPUT_TEXT); // debounced
  const citiesFilter = searchParams.get(PROP_NAME_CITY);
  const searchToDate = searchParams.get(PROP_NAME_TO_DATE_SEARCH_MAIN);
  const searchFromDate = searchParams.get(PROP_NAME_FROM_DATE_SEARCH_MAIN);
  const searchPurpose = searchParams.get(PROP_NAME_PURPOSE);
  const searchPaymentMethod = searchParams.get(PROP_NAME_METHOD);

  useEffect(() => {
    if (searchText && isUserInput) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(PROP_NAME_SKIP_ADS);
      setSearchParams(newSearchParams, { replace: true });
    } else {
      setIsUserInput(true);
    }
  }, [searchText]);

  useEffect(() => {
    if (skipAdsFilter) {
      setSkipAds(+skipAdsFilter);
    } else {
      setSkipAds(0);
    }
  }, [skipAdsFilter]);

  const {
    loading,
    data: transactionData,
    refreshAsync: refreshTransactions,
  } = useRequest(
    () =>
      getTransactions(
        {
          pagination: {
            skip: skipAds,
            take: ENTITY_PER_PAGE,
            total: pagination.total,
          },
          transactionsType: status,
          searchText,
          cities: citiesFilter?.length ? citiesFilter.split(",") : [],
          dateEnd: searchToDate,
          dateStart: searchFromDate,
          sort: { sortBy },
          purpose: (searchPurpose || "") as GetPurposeCategoryEnum,
          paymentMethod:
            searchPaymentMethod?.length && searchPaymentMethod?.split(",").length < 2
              ? (searchPaymentMethod.split(",") as PaymentMethod[])
              : [],
        },
        localCountry.iso.toUpperCase(),
      ),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry, isRefresh, sortBy],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: (data) => {
        setPagination((prev) => ({ ...prev, total: +data.totalCount }));
      },
    },
  );

  const {
    loading: isMetricsLoading,
    data: metrics,
    refreshAsync: refreshTransactionMetrics,
  } = useRequest(
    () =>
      getTransactionMetrics(
        {
          transactionsType: status,
          searchText,
          cities: citiesFilter?.length ? citiesFilter.split(",") : [],
          dateEnd: searchToDate,
          dateStart: searchFromDate,
          purpose: (searchPurpose || "") as GetPurposeCategoryEnum,
          paymentMethod:
            searchPaymentMethod?.length && searchPaymentMethod?.split(",").length < 2
              ? (searchPaymentMethod.split(",") as PaymentMethod[])
              : [],
        },
        localCountry.iso.toUpperCase(),
      ),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry, isRefresh],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
    },
  );

  const onSortBy = useCallback(
    (sortName: string) => {
      onSort(sortName, searchParams, sortBy, setSearchParams);
    },
    [searchParams, setSearchParams, sortBy],
  );

  const { loading: isRetryWithNewPayoutLoading, runAsync: onRetryWithNewPayout } = useRequest(
    (transactionId: string) => retryWithNewPayout(transactionId, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      manual: true,
      refreshDeps: [localCountry],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: () => {
        transactionIdOnAction.current = "";
      },
    },
  );

  const { loading: isSoftRetryLoading, runAsync: onSoftRetry } = useRequest(
    (transactionId: string) => softRetryPayout(transactionId, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      manual: true,
      refreshDeps: [localCountry],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: () => {
        transactionIdOnAction.current = "";
      },
    },
  );

  const { loading: isForceCompleteLoading, runAsync: onForceComplete } = useRequest(
    (transactionId: string) => forceCompleteTransaction(transactionId, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      manual: true,
      refreshDeps: [localCountry],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: () => {
        transactionIdOnAction.current = "";
      },
    },
  );

  const { loading: isForceCancelLoading, runAsync: onForceCancel } = useRequest(
    (transactionId: string) => forceCancelTransaction(transactionId, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      manual: true,
      refreshDeps: [localCountry],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
      onSuccess: () => {
        transactionIdOnAction.current = "";
      },
    },
  );
  console.log(loading, transactionData, isMetricsLoading, metrics);

  const columns = useMemo(
    () => [
      { title: "In/Out", dataIndex: "inOut", key: "inOut" },
      {
        title: (
          <div className={`${styles.idColumn} ${styles.mainStyleColumn}`}>
            <img src={hash} alt="hash" />
            <div>ID</div>
          </div>
        ),
        dataIndex: "id",
        key: "id",
      },
      {
        title: (
          <div className={`${styles.mainStyleColumn}`}>
            <img src={calendarDots} alt="calendar dots" />
            <div>Date</div>
            <Button
              type="text"
              icon={
                <img
                  src={sortIcon}
                  alt="Date"
                  style={{ width: 16, height: 16, transform: sortBy === "created_at" ? "rotate(180deg)" : null }}
                />
              }
              onClick={() => {
                onSortBy("created_at");
              }}
            />
          </div>
        ),
        key: "date",
        dataIndex: "date",
      },
      {
        title: (
          <div className={`${styles.mainStyleColumn}`}>
            <img src={userCircle} alt="calendar dots" />
            <div>User</div>
          </div>
        ),
        key: "user",
        dataIndex: "user",
      },
      {
        title: (
          <div className={`${styles.mainStyleColumn}`}>
            <img src={hash} alt="calendar dots" />
            <div>Deal ID</div>
          </div>
        ),
        key: "dealId",
        dataIndex: "dealId",
      },
      {
        title: (
          <div className={`${styles.mainStyleColumn}`}>
            <img src={crosshair} alt="calendar dots" />
            <div>Purpose</div>
          </div>
        ),
        key: "purpose",
        dataIndex: "purpose",
      },
      {
        title: (
          <div className={`${styles.mainStyleColumn}`}>
            <img src={currencyEur} alt="euro" />
            <div>Amount</div>
            <Button
              type="text"
              icon={
                <img
                  src={sortIcon}
                  alt="amount"
                  style={{ width: 16, height: 16, transform: sortBy === "amount" ? "rotate(180deg)" : null }}
                />
              }
              onClick={() => {
                onSortBy("amount");
              }}
            />
          </div>
        ),
        key: "amount",
        dataIndex: "amount",
      },
      {
        title: (
          <div className={`${styles.mainStyleColumn}`}>
            <img src={spinnerGap} alt="spinner" />
            <div>Status</div>
          </div>
        ),
        key: "status",
        dataIndex: "status",
      },
      {
        title: "",
        dataIndex: "actions",
        key: "actions",
      },
    ],
    [sortBy],
  );

  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const onCloseConfirmModal = () => {
    setIsModalConfirmOpen(false);
  };
  const onOpenConfirmModal = () => {
    setIsModalConfirmOpen(true);
  };

  const [infoModalConfirm, setInfoModalConfirm] = useState<{
    type: TransactionAction;
    transactionId: string;
  }>(null);

  const source = useMemo(() => {
    if (!transactionData) return [];
    return transactionData?.transactions.map((el) => {
      const date = new Date(el.createdAt);

      return {
        key: el.transactionId,
        inOut: (
          <div className={styles.inOutField}>
            <img src={el.processType === ProcessType.INCOMING ? arrowDownGreen : arrowUpRed} alt="arrow" />
            <div>{el.processType === ProcessType.INCOMING ? "In" : "Out"}</div>
          </div>
        ),
        id: (
          <Tooltip title={el.transactionId}>
            <div className={styles.idField}>{`...${el?.transactionId?.slice(-5)}`}</div>
          </Tooltip>
        ),
        date: <div className={styles.dateField}>{formatTransactionDateTime(date)}</div>,
        user: (
          <UserNameRatingPhotoComponent
            el={{
              avatar: el.user.avatar,
              firstName: el.user.name,
              rating: el.user.rating,
              reviewCount: el.user.review_count,
              lastName: "",
              online: el.user.online,
              isOnlyName: true,
              lastOnline: el.user.lastOnline,
            }}
            avatarSize={32}
          />
        ),
        dealId: el.rentId && (
          <Tooltip title={el.rentId}>
            <div className={styles.dealIdField}>{`...${el?.rentId?.slice(-5)}`}</div>
          </Tooltip>
        ),
        purpose: <div className={styles.purposeField}>{PURPOSE_OPTIONS_V2[el.purposeCategory]}</div>,
        amount: (
          <div className={styles.amountField}>
            <div>{el.paymentMethod === "Card" ? "€" : <img src={c2cToken} alt="c2c token" />}</div>
            <div>{el.amount.toFixed(2)}</div>
          </div>
        ),
        status: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {divStatuses[el.status]}{" "}
            {el.availableActions.map((v) => {
              let isBtnLoading = false;

              if (el.transactionId === transactionIdOnAction.current) {
                if (v === TransactionAction.COMPLETE) {
                  isBtnLoading = isForceCompleteLoading;
                }

                if (v === TransactionAction.RECREATE_PAYMENT) {
                  isBtnLoading = isRetryWithNewPayoutLoading;
                }

                if (v === TransactionAction.RETRY) {
                  isBtnLoading = isSoftRetryLoading;
                }

                if (v === TransactionAction.CANCEL) {
                  isBtnLoading = isForceCancelLoading;
                }
              }
              return (
                <CustomButton
                  key={v + el.transactionId}
                  sizeButton="medium"
                  typeButton={v === TransactionAction.COMPLETE ? "critical" : "secondary"}
                  loading={isBtnLoading}
                  onClick={async () => {
                    setInfoModalConfirm({
                      transactionId: el.transactionId,
                      type: v,
                    });
                    onOpenConfirmModal();
                  }}
                >
                  {v === TransactionAction.RECREATE_PAYMENT ? "Retry" : v[0].toUpperCase() + v.slice(1)}
                </CustomButton>
              );
            })}
          </div>
        ),
        actions: (
          <div style={{ display: "flex", alignContent: "center", justifyContent: "end" }}>
            <CustomButton
              sizeButton="medium"
              typeButton="primary"
              onClick={() => {
                setIsDealOpen(true);
                setSelectedId(el.rentId);
              }}
            >
              Details
            </CustomButton>
          </div>
        ),
      };
    });
  }, [transactionData?.transactions]);

  return (
    <div className={styles.wrapper}>
      <div style={{ display: "flex", gap: 10 }}>
        {[
          {
            infoDate: "Transactions amount in c2c tokens and euros",
            label: "Amount",
            value: (
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <img src={c2cToken} alt="c2cToken" />
                <span style={{ lineHeight: "0" }}>{formatNumber(metrics?.wallet_amount)}</span>
              </div>
            ),
            loading: isMetricsLoading,
            moreInfo: metrics?.wallet_amount_change
              ? {
                  value: `${metrics.wallet_amount_change > 0 ? "+" : ""}${formatNumber(metrics.wallet_amount_change)}%`,
                  isPositive: metrics.wallet_amount_change > 0,
                }
              : null,
            moreValue: {
              value: formatMoney(metrics?.bank_amount),
              moreInfo: metrics?.bank_amount_change
                ? {
                    value: `${metrics.bank_amount_change > 0 ? "+" : ""}${formatNumber(metrics.bank_amount_change)}%`,
                    isPositive: metrics.bank_amount_change > 0,
                  }
                : null,
            },
          },
          {
            infoDate: "Service Fees",
            label: "Service Fees",
            value: formatMoney(metrics?.service_fee),
            loading: isMetricsLoading,
            moreInfo: metrics?.service_fee_change
              ? {
                  value: `${metrics.service_fee_change > 0 ? "+" : ""}${formatNumber(metrics.service_fee_change)}%`,
                  isPositive: metrics.service_fee_change > 0,
                }
              : null,
          },
          {
            infoDate: "Value-added tax",
            label: "VAT",
            value: formatMoney(metrics?.vat),
            loading: isMetricsLoading,
            moreInfo: metrics?.vat_change
              ? {
                  value: `${metrics.vat_change > 0 ? "+" : ""}${formatNumber(metrics.vat_change)}%`,
                  isPositive: metrics.vat_change > 0,
                }
              : null,
          },
        ].map(({ infoDate, label, value, loading, moreValue, moreInfo }, i) => (
          <InfoComponent
            key={i}
            infoOfData={infoDate}
            label={label}
            value={value}
            isLoading={loading}
            moreValue={moreValue}
            moreInfo={moreInfo}
          />
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        <TableAntdV2 columns={columns} dataSource={source} loading={loading} className={styles.table} />
      </div>
      <div style={{ marginTop: 40 }}>
        <CustomPagination
          hideOnSinglePage
          showSizeChanger={false}
          total={transactionData?.totalCount}
          current={Math.floor(skipAds / ENTITY_PER_PAGE + 1)}
          onChange={(page) => {
            const pageMinus = page - 1;
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set(PROP_NAME_SKIP_ADS, (pageMinus * ENTITY_PER_PAGE).toString());
            setSearchParams(newSearchParams, { replace: true });
          }}
        />
      </div>
      <DealsInfo
        isOpen={isDealOpen}
        close={() => {
          setIsDealOpen(false);
        }}
        id={selectedId}
      />

      <ModalConfirm
        isOpen={isModalConfirmOpen}
        onClose={onCloseConfirmModal}
        isActionLoading={
          isSoftRetryLoading || isForceCancelLoading || isForceCompleteLoading || isRetryWithNewPayoutLoading
        }
        type={infoModalConfirm?.type}
        onAction={async () => {
          if (!infoModalConfirm) return;

          if (infoModalConfirm.type === TransactionAction.COMPLETE) {
            await onForceComplete(infoModalConfirm.transactionId);
            transactionIdOnAction.current = infoModalConfirm.transactionId;
          }

          if (infoModalConfirm.type === TransactionAction.RECREATE_PAYMENT) {
            await onRetryWithNewPayout(infoModalConfirm.transactionId);
            transactionIdOnAction.current = infoModalConfirm.transactionId;
          }

          if (infoModalConfirm.type === TransactionAction.RETRY) {
            await onSoftRetry(infoModalConfirm.transactionId);
            transactionIdOnAction.current = infoModalConfirm.transactionId;
          }

          if (infoModalConfirm.type === TransactionAction.CANCEL) {
            await onForceCancel(infoModalConfirm.transactionId);
            transactionIdOnAction.current = infoModalConfirm.transactionId;
          }

          onCloseConfirmModal();

          refreshTransactions();
          refreshTransactionMetrics();
        }}
      />
    </div>
  );
};
